/**
 * Structured AU address form for onboarding (Slice B — Feb 2026 July).
 *
 * Layout:
 *   ┌── Address line 1 (Google Places autocomplete) ──┐
 *   │  Address line 2 (optional)                       │
 *   │  Suburb                State ▼    Postcode      │
 *   └──────────────────────────────────────────────────┘
 *
 * When the user selects a Google suggestion we extract the structured
 * components (suburb, state, postcode) so the discrete fields fill
 * themselves. If autocomplete falls back to manual entry, every field
 * remains editable.
 *
 * `value` is a `{line1, line2, suburb, state, postcode, formatted}` dict.
 * `onChange(next)` receives the same shape on every keystroke/selection.
 */
import { useCallback, useRef, useState } from "react";
import { Input } from "@/components/ui/input";

const GOOGLE_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
const AU_STATES = ["ACT", "NSW", "NT", "QLD", "SA", "TAS", "VIC", "WA"];
// Feb 2026 — countries currently supported for structured storage.
// Restricted to AU today (Places autocomplete is AU-only via
// includedRegionCodes) but the field is captured explicitly so future
// Starshipit / shipping integrations don't need a code change.
const COUNTRIES = [
  { code: "AU", label: "Australia" },
];

let _loadPromise = null;
function loadGoogleMaps() {
  if (typeof window === "undefined" || !GOOGLE_KEY) return Promise.reject(new Error("no-key"));
  if (typeof window.google?.maps?.importLibrary === "function") return Promise.resolve(window.google);
  if (_loadPromise) return _loadPromise;
  _loadPromise = new Promise((resolve, reject) => {
    const start = Date.now();
    const waitReady = () => {
      if (typeof window.google?.maps?.importLibrary === "function") { resolve(window.google); return; }
      if (Date.now() - start > 10000) { _loadPromise = null; reject(new Error("timeout")); return; }
      setTimeout(waitReady, 50);
    };
    if (document.querySelector("script[data-scwt-google-maps]")) { waitReady(); return; }
    const s = document.createElement("script");
    s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(GOOGLE_KEY)}&libraries=places&loading=async&v=weekly`;
    s.async = true; s.defer = true;
    s.setAttribute("data-scwt-google-maps", "1");
    s.onload = waitReady;
    s.onerror = () => { _loadPromise = null; reject(new Error("script-error")); };
    document.head.appendChild(s);
  });
  return _loadPromise;
}

let _placesLib = null;
let _sessionToken = null;
async function getPlacesLib() {
  if (_placesLib) return _placesLib;
  const google = await loadGoogleMaps();
  _placesLib = await google.maps.importLibrary("places");
  return _placesLib;
}
function newSessionToken() {
  if (_placesLib?.AutocompleteSessionToken) _sessionToken = new _placesLib.AutocompleteSessionToken();
  return _sessionToken;
}

/** Map Google `AddressComponent` list → SCWT structured address dict. */
function extractComponents(components, formatted) {
  const get = (type) => (components || []).find((c) => (c.types || []).includes(type));
  const streetNumber = get("street_number")?.longText || "";
  const route        = get("route")?.longText || "";
  const subpremise   = get("subpremise")?.longText || "";
  const line1        = [streetNumber, route].filter(Boolean).join(" ").trim();
  const line2        = subpremise ? `Unit ${subpremise}` : "";
  const suburb       = get("locality")?.longText || get("sublocality")?.longText || "";
  const state        = (get("administrative_area_level_1")?.shortText || "").toUpperCase();
  const postcode     = get("postal_code")?.longText || "";
  // Feb 2026 — country component. We fall back to "AU" because
  // includedRegionCodes restricts autocomplete to Australia; if a
  // country component is returned we still normalise to its ISO code.
  const country      = (get("country")?.shortText || "AU").toUpperCase();
  return { line1, line2, suburb, state, postcode, country, formatted: formatted || "" };
}

export default function AddressStructuredForm({
  value,
  onChange,
  testIdPrefix = "addr",
  disabled = false,
}) {
  const v = value || { line1: "", line2: "", suburb: "", state: "", postcode: "", country: "AU", formatted: "" };
  const [suggestions, setSuggestions] = useState([]);
  const [showList, setShowList] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [autoState, setAutoState] = useState(GOOGLE_KEY ? "ready" : "manual");
  const debounceRef = useRef(null);
  const selectingRef = useRef(false);

  const patch = (partial) => onChange({ ...v, ...partial });

  const fetchSuggestions = useCallback(async (q) => {
    if (!q || q.length < 3 || !GOOGLE_KEY) { setSuggestions([]); return; }
    try {
      const places = await getPlacesLib();
      if (!_sessionToken) newSessionToken();
      const { AutocompleteSuggestion } = places;
      const { suggestions: results } = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input: q,
        includedRegionCodes: ["au"],
        sessionToken: _sessionToken,
      });
      const list = (results || [])
        .filter((s) => s.placePrediction)
        .map((s) => ({
          id: s.placePrediction.placeId,
          mainText: s.placePrediction.mainText?.text || "",
          secondaryText: s.placePrediction.secondaryText?.text || "",
          text: s.placePrediction.text?.text || "",
          prediction: s.placePrediction,
        }));
      setSuggestions(list);
      setShowList(list.length > 0);
      setActiveIdx(-1);
      setAutoState("ready");
    } catch {
      setAutoState("manual");
      setSuggestions([]);
      setShowList(false);
    }
  }, []);

  const handleLine1Change = (e) => {
    const next = e.target.value;
    patch({ line1: next });
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(next), 200);
  };

  const selectSuggestion = async (s) => {
    if (!s?.prediction) return;
    selectingRef.current = true;
    setShowList(false);
    try {
      const place = s.prediction.toPlace();
      await place.fetchFields({ fields: ["formattedAddress", "addressComponents"] });
      const extracted = extractComponents(place.addressComponents, place.formattedAddress);
      onChange(extracted);
      newSessionToken();
    } catch {
      patch({ line1: s.text });
    } finally {
      setTimeout(() => { selectingRef.current = false; }, 100);
    }
  };

  const handleKeyDown = (e) => {
    if (!showList || suggestions.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => (i + 1) % suggestions.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => (i - 1 + suggestions.length) % suggestions.length); }
    else if (e.key === "Enter" && activeIdx >= 0) { e.preventDefault(); selectSuggestion(suggestions[activeIdx]); }
    else if (e.key === "Escape") setShowList(false);
  };

  const handleBlur = () => {
    setTimeout(() => { if (!selectingRef.current) setShowList(false); }, 150);
  };

  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-form`}>
      <div className="relative">
        <label className="text-xs font-medium text-slate-600">Address line 1 *</label>
        <Input
          data-testid={`${testIdPrefix}-line1`}
          value={v.line1}
          onChange={handleLine1Change}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onFocus={() => suggestions.length > 0 && setShowList(true)}
          placeholder="12 Coastal Road"
          disabled={disabled}
          autoComplete="off"
          className="mt-1"
        />
        {showList && suggestions.length > 0 && (
          <div
            data-testid={`${testIdPrefix}-suggestions`}
            className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden max-h-60 overflow-y-auto"
          >
            {suggestions.map((s, i) => (
              <button
                key={s.id || i}
                type="button"
                data-testid={`${testIdPrefix}-suggestion-${i}`}
                onMouseDown={(e) => { e.preventDefault(); selectingRef.current = true; }}
                onClick={() => selectSuggestion(s)}
                onMouseEnter={() => setActiveIdx(i)}
                className={`w-full text-left px-3 py-2 text-sm border-b border-slate-100 last:border-0 transition-colors ${
                  i === activeIdx ? "bg-cyan-50 text-cyan-900" : "hover:bg-slate-50 text-slate-800"
                }`}
              >
                <div className="font-medium">{s.mainText}</div>
                {s.secondaryText && <div className="text-xs text-slate-500 mt-0.5">{s.secondaryText}</div>}
              </button>
            ))}
          </div>
        )}
        {autoState === "manual" && GOOGLE_KEY && (
          <p className="mt-1 text-[11px] text-slate-400">
            Address suggestions unavailable — type each field manually below.
          </p>
        )}
      </div>

      <div>
        <label className="text-xs font-medium text-slate-600">Address line 2 (unit / suite)</label>
        <Input
          data-testid={`${testIdPrefix}-line2`}
          value={v.line2}
          onChange={(e) => patch({ line2: e.target.value })}
          placeholder="Suite 3"
          disabled={disabled}
          className="mt-1"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-6 gap-3">
        <div className="sm:col-span-3">
          <label className="text-xs font-medium text-slate-600">Suburb *</label>
          <Input
            data-testid={`${testIdPrefix}-suburb`}
            value={v.suburb}
            onChange={(e) => patch({ suburb: e.target.value })}
            placeholder="Noosaville"
            disabled={disabled}
            className="mt-1"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs font-medium text-slate-600">State *</label>
          <select
            data-testid={`${testIdPrefix}-state`}
            value={v.state}
            onChange={(e) => patch({ state: e.target.value })}
            disabled={disabled}
            className="mt-1 w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm text-slate-800"
          >
            <option value="">Select…</option>
            {AU_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="sm:col-span-1">
          <label className="text-xs font-medium text-slate-600">Postcode *</label>
          <Input
            data-testid={`${testIdPrefix}-postcode`}
            value={v.postcode}
            onChange={(e) => patch({ postcode: e.target.value.replace(/\D/g, "").slice(0, 4) })}
            placeholder="4566"
            disabled={disabled}
            inputMode="numeric"
            className="mt-1 font-mono"
          />
        </div>
      </div>

      <div>
        <label className="text-xs font-medium text-slate-600">Country</label>
        <select
          data-testid={`${testIdPrefix}-country`}
          value={v.country || "AU"}
          onChange={(e) => patch({ country: e.target.value.toUpperCase() })}
          disabled={disabled}
          className="mt-1 w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm text-slate-800"
        >
          {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
        </select>
      </div>
    </div>
  );
}
